/**
 * API Endpoints (webhooks incoming) — Provider + hook compartilhados.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ApiEndpoint } from '../../../../types';
import { apiEndpointsRepository } from '../../../../infrastructure/repositories/SupabaseApiEndpointsRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { createModuleLogger } from '../../../../utils/logger';

const log = createModuleLogger('[ApiEndpoints]');

interface ApiEndpointsContextData {
    endpoints: ApiEndpoint[];
    loading: boolean;
    createEndpoint: (
        data: Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'>
    ) => Promise<ApiEndpoint>;
    updateEndpoint: (id: string, data: Partial<ApiEndpoint>) => Promise<ApiEndpoint>;
    deleteEndpoint: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    regenerateToken: (id: string) => Promise<string>;
    selectedIds: Set<string>;
    toggleSelection: (id: string) => void;
    toggleAllSelection: (filteredEndpoints: ApiEndpoint[]) => void;
    clearSelection: () => void;
    refetch: () => Promise<void>;
    getEndpointUrl: (slug: string) => string;
    generateToken: () => string;
    generateSlug: (name: string) => string;
}

const ApiEndpointsContext = createContext<ApiEndpointsContextData | undefined>(undefined);

export const ApiEndpointsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const loadEndpoints = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const data = await apiEndpointsRepository.findAll(currentOrganization.id);
            setEndpoints(data);
        } catch (error) {
            log.error('Error loading API endpoints:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (currentOrganization?.id) {
            loadEndpoints();
        }
    }, [currentOrganization?.id, loadEndpoints]);

    const createEndpoint = useCallback(async (
        data: Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'>
    ): Promise<ApiEndpoint> => {
        if (!currentOrganization?.id) throw new Error('Organização não encontrada');

        setLoading(true);
        try {
            const newEndpoint = await apiEndpointsRepository.create(data, currentOrganization.id);
            setEndpoints(prev => [newEndpoint, ...prev]);
            return newEndpoint;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const updateEndpoint = useCallback(async (id: string, data: Partial<ApiEndpoint>): Promise<ApiEndpoint> => {
        if (!currentOrganization?.id) throw new Error('Organização não encontrada');

        setLoading(true);
        try {
            const updatedEndpoint = await apiEndpointsRepository.update(id, data, currentOrganization.id);
            setEndpoints(prev => prev.map(ep => (ep.id === id ? updatedEndpoint : ep)));
            return updatedEndpoint;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const deleteEndpoint = useCallback(async (id: string): Promise<void> => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            await apiEndpointsRepository.delete(id, currentOrganization.id);
            setEndpoints(prev => prev.filter(ep => ep.id !== id));
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const regenerateToken = useCallback(async (id: string): Promise<string> => {
        if (!currentOrganization?.id) throw new Error('Organização não encontrada');

        const newToken = await apiEndpointsRepository.regenerateToken(id, currentOrganization.id);
        setEndpoints(prev => prev.map(ep =>
            ep.id === id ? { ...ep, authToken: newToken } : ep
        ));
        return newToken;
    }, [currentOrganization?.id]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback((filteredEndpoints: ApiEndpoint[]) => {
        setSelectedIds(prev => {
            if (prev.size === filteredEndpoints.length && filteredEndpoints.length > 0) {
                return new Set();
            }
            return new Set(filteredEndpoints.map(ep => ep.id));
        });
    }, []);

    const deleteBulk = useCallback(async (ids: string[]): Promise<void> => {
        if (!currentOrganization?.id || ids.length === 0) return;

        setLoading(true);
        try {
            await Promise.all(ids.map(id => apiEndpointsRepository.delete(id, currentOrganization.id!)));
            setEndpoints(prev => prev.filter(ep => !ids.includes(ep.id)));
            setSelectedIds(new Set());
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const getEndpointUrl = useCallback((slug: string): string => {
        const customDomain = import.meta.env.VITE_WEBHOOK_DOMAIN;
        if (customDomain) {
            return `${customDomain.replace(/\/$/, '')}/api-webhook/${slug}`;
        }
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
        return `${supabaseUrl}/functions/v1/api-webhook/${slug}`;
    }, []);

    const generateToken = useCallback((): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const tokenLength = 32;
        let token = '';
        const randomValues = new Uint8Array(tokenLength);
        crypto.getRandomValues(randomValues);
        for (let i = 0; i < tokenLength; i++) {
            token += chars[randomValues[i] % chars.length];
        }
        return token;
    }, []);

    const generateSlug = useCallback((name: string): string => {
        return name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 50);
    }, []);

    const value: ApiEndpointsContextData = {
        endpoints,
        loading,
        createEndpoint,
        updateEndpoint,
        deleteEndpoint,
        deleteBulk,
        regenerateToken,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch: loadEndpoints,
        getEndpointUrl,
        generateToken,
        generateSlug,
    };

    return React.createElement(ApiEndpointsContext.Provider, { value }, children);
};

export const useApiEndpoints = (): ApiEndpointsContextData => {
    const context = useContext(ApiEndpointsContext);
    if (!context) {
        throw new Error('useApiEndpoints must be used within ApiEndpointsProvider');
    }
    return context;
};
